import React, { useEffect, useState } from 'react';
import {
    AlertTriangle,
    Building2,
    CheckCircle,
    ChevronDown,
    GitBranch,
    GitMerge,
    Loader,
    Plus,
    X,
} from 'lucide-react';
import superAdminAPI from '../services/superAdminAPI';

/**
 * AssignToGroupModal
 *
 * Allows the super admin to assign one or more existing `single` schools to a
 * school group, either an existing `group_parent` or a brand-new group created
 * on the spot.
 *
 * Props:
 *   isOpen        {boolean}  – whether the modal is visible
 *   onClose       {func}     – called when the modal should close
 *   onSuccess     {func}     – called with the result after a successful operation
 *   schools       {Array}    – array of school objects to be assigned
 */
const AssignToGroupModal = ({ isOpen, onClose, onSuccess, schools = [] }) => {
    // ── Mode ──────────────────────────────────────────────────────────────────
    const [mode, setMode] = useState('existing'); // 'existing' | 'new'

    // ── Existing group state ──────────────────────────────────────────────────
    const [groups, setGroups] = useState([]);
    const [groupsLoading, setGroupsLoading] = useState(false);
    const [groupsError, setGroupsError] = useState(null);
    const [selectedGroupId, setSelectedGroupId] = useState('');

    // ── New group form state ──────────────────────────────────────────────────
    const [groupName, setGroupName] = useState('');
    const [adminName, setAdminName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [groupDomain, setGroupDomain] = useState('');

    // ── Submission state ──────────────────────────────────────────────────────
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    // ── Fetch groups when mode = 'existing' and modal opens ───────────────────
    useEffect(() => {
        if (!isOpen) return;
        resetForm();
        if (mode === 'existing') loadGroups();
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (mode === 'existing' && isOpen && groups.length === 0 && !groupsLoading) {
            loadGroups();
        }
    }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadGroups = async () => {
        setGroupsLoading(true);
        setGroupsError(null);
        try {
            const data = await superAdminAPI.getSchoolGroups({ limit: 200 });
            setGroups(Array.isArray(data) ? data : []);
        } catch (err) {
            setGroupsError(superAdminAPI.handleError(err));
        } finally {
            setGroupsLoading(false);
        }
    };

    const resetForm = () => {
        setMode('existing');
        setSelectedGroupId('');
        setGroupName('');
        setAdminName('');
        setAdminEmail('');
        setGroupDomain('');
        setSubmitError(null);
        setSubmitSuccess(false);
        setSuccessMessage('');
    };

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = () => {
        if (schools.length === 0) return 'No schools selected.';
        if (mode === 'existing') {
            if (!selectedGroupId) return 'Please select a target group.';
        } else {
            if (!groupName.trim()) return 'Group name is required.';
            if (!adminName.trim()) return 'Admin name is required.';
            if (!adminEmail.trim()) return 'Admin email is required.';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(adminEmail.trim())) return 'Please enter a valid email address.';
        }
        return null;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        const validationError = validate();
        if (validationError) {
            setSubmitError(validationError);
            return;
        }

        setSubmitting(true);
        setSubmitError(null);

        try {
            if (mode === 'existing') {
                // Assign each school individually
                const schoolIds = schools.map((s) => s.id);
                const results = await Promise.allSettled(
                    schoolIds.map((sid) => superAdminAPI.assignSchoolToGroup(sid, selectedGroupId))
                );

                const succeeded = results.filter((r) => r.status === 'fulfilled');
                const failed = results.filter((r) => r.status === 'rejected');

                if (failed.length > 0 && succeeded.length === 0) {
                    throw new Error(
                        failed[0].reason?.response?.data?.detail ||
                        failed[0].reason?.message ||
                        'All assignments failed.'
                    );
                }

                if (failed.length > 0) {
                    const targetGroup = groups.find((g) => g.id === selectedGroupId);
                    setSuccessMessage(
                        `${succeeded.length} school(s) assigned to "${targetGroup?.name || 'group'}" successfully. ` +
                        `${failed.length} school(s) could not be assigned.`
                    );
                } else {
                    const targetGroup = groups.find((g) => g.id === selectedGroupId);
                    setSuccessMessage(
                        `${succeeded.length} school(s) successfully assigned to "${targetGroup?.name || 'group'}".`
                    );
                }

                setSubmitSuccess(true);
                onSuccess && onSuccess(succeeded.map((r) => r.value));
            } else {
                // Create new group from existing schools
                const result = await superAdminAPI.createGroupFromExisting({
                    group_name: groupName.trim(),
                    group_admin_name: adminName.trim(),
                    group_admin_email: adminEmail.trim(),
                    school_ids: schools.map((s) => s.id),
                    domain: groupDomain.trim() || undefined,
                });

                setSuccessMessage(
                    `Group "${result.name}" created and ${schools.length} school(s) assigned as branches.`
                );
                setSubmitSuccess(true);
                onSuccess && onSuccess([result]);
            }
        } catch (err) {
            setSubmitError(
                err?.response?.data?.detail ||
                err?.message ||
                'An unexpected error occurred.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (submitting) return;
        resetForm();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
                {/* ── Header ── */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                            <GitMerge size={20} className="text-purple-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-800">Assign to Group</h2>
                            <p className="text-sm text-slate-500">
                                {schools.length === 1
                                    ? `Assigning 1 school to a group`
                                    : `Assigning ${schools.length} schools to a group`}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        disabled={submitting}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* ── Schools being assigned ── */}
                    <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">
                            Schools to be assigned:
                        </p>
                        <div className="bg-slate-50 rounded-xl p-3 max-h-36 overflow-y-auto space-y-2">
                            {schools.map((school) => (
                                <div key={school.id} className="flex items-center gap-2">
                                    <Building2 size={14} className="text-slate-400 flex-shrink-0" />
                                    <span className="text-sm text-slate-700 font-medium">
                                        {school.name}
                                    </span>
                                    {school.domain && (
                                        <span className="text-xs text-slate-400">
                                            ({school.domain})
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Success state ── */}
                    {submitSuccess ? (
                        <div className="text-center py-4 space-y-4">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                                <CheckCircle size={32} className="text-green-600" />
                            </div>
                            <p className="text-green-700 font-medium">{successMessage}</p>
                            <button
                                onClick={handleClose}
                                className="px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium"
                            >
                                Done
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* ── Mode Toggle ── */}
                            <div>
                                <p className="text-sm font-medium text-slate-700 mb-3">
                                    How would you like to assign?
                                </p>
                                <div className="grid grid-cols-2 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setMode('existing')}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-left ${
                                            mode === 'existing'
                                                ? 'border-purple-500 bg-purple-50'
                                                : 'border-gray-200 bg-white hover:border-gray-300'
                                        }`}
                                    >
                                        <GitBranch
                                            size={22}
                                            className={mode === 'existing' ? 'text-purple-600' : 'text-gray-400'}
                                        />
                                        <span
                                            className={`text-sm font-medium ${
                                                mode === 'existing' ? 'text-purple-700' : 'text-gray-600'
                                            }`}
                                        >
                                            Existing Group
                                        </span>
                                        <span className="text-xs text-center text-gray-400 leading-tight">
                                            Add to an existing school group
                                        </span>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => setMode('new')}
                                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all text-left ${
                                            mode === 'new'
                                                ? 'border-orange-500 bg-orange-50'
                                                : 'border-gray-200 bg-white hover:border-gray-300'
                                        }`}
                                    >
                                        <Plus
                                            size={22}
                                            className={mode === 'new' ? 'text-orange-600' : 'text-gray-400'}
                                        />
                                        <span
                                            className={`text-sm font-medium ${
                                                mode === 'new' ? 'text-orange-700' : 'text-gray-600'
                                            }`}
                                        >
                                            Create New Group
                                        </span>
                                        <span className="text-xs text-center text-gray-400 leading-tight">
                                            Form a new group from these schools
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {/* ── Existing Group Form ── */}
                            {mode === 'existing' && (
                                <div className="space-y-3">
                                    <label className="block text-sm font-medium text-slate-700">
                                        Select Target Group
                                    </label>
                                    {groupsLoading ? (
                                        <div className="flex items-center gap-2 text-gray-500 text-sm py-3">
                                            <Loader size={16} className="animate-spin" />
                                            Loading groups...
                                        </div>
                                    ) : groupsError ? (
                                        <div className="text-red-600 text-sm flex items-center gap-2 py-2">
                                            <AlertTriangle size={14} />
                                            {groupsError}
                                            <button
                                                onClick={loadGroups}
                                                className="underline ml-1"
                                            >
                                                Retry
                                            </button>
                                        </div>
                                    ) : groups.length === 0 ? (
                                        <div className="text-gray-500 text-sm py-2 bg-gray-50 rounded-lg px-3">
                                            No existing groups found. Create a new group instead.
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            <select
                                                value={selectedGroupId}
                                                onChange={(e) => setSelectedGroupId(e.target.value)}
                                                className="w-full px-4 py-3 pr-10 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors appearance-none bg-white"
                                            >
                                                <option value="">— Select a group —</option>
                                                {groups.map((g) => (
                                                    <option key={g.id} value={g.id}>
                                                        {g.name}
                                                        {g.domain ? ` (${g.domain})` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronDown
                                                size={16}
                                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── New Group Form ── */}
                            {mode === 'new' && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            Group Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={groupName}
                                            onChange={(e) => setGroupName(e.target.value)}
                                            placeholder="e.g. Sunshine Academy Group"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            Group Admin Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={adminName}
                                            onChange={(e) => setAdminName(e.target.value)}
                                            placeholder="e.g. John Smith"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            Group Admin Email <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="email"
                                            value={adminEmail}
                                            onChange={(e) => setAdminEmail(e.target.value)}
                                            placeholder="admin@school.com"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">
                                            Group Domain
                                            <span className="text-gray-400 font-normal ml-1">(optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={groupDomain}
                                            onChange={(e) => setGroupDomain(e.target.value)}
                                            placeholder="e.g. sunshinegroup"
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* ── Error ── */}
                            {submitError && (
                                <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
                                    <AlertTriangle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                                    <p className="text-red-700 text-sm">{submitError}</p>
                                </div>
                            )}

                            {/* ── Actions ── */}
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    disabled={submitting}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors font-medium disabled:opacity-40"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    disabled={submitting || (mode === 'existing' && groups.length === 0 && !groupsLoading)}
                                    className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl hover:from-purple-600 hover:to-purple-700 transition-all font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader size={16} className="animate-spin" />
                                            Assigning...
                                        </>
                                    ) : (
                                        <>
                                            <GitMerge size={16} />
                                            {mode === 'existing' ? 'Assign to Group' : 'Create & Assign'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AssignToGroupModal;
